'use client';

import React, { useCallback, useMemo, useState } from 'react';
import { Box, Button, MenuItem, Paper, TextField, Typography } from '@mui/material';
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import GenericFormModal from '../form/GenericFormModal';
import { GENERIC_FORM_FIELD_TYPE } from '../form/GenericForm';
import GenericActionPicker, { GENERIC_ICON_NAME } from '../form/GenericActionPicker';
import { useNotification } from '@/contexts/NotificationContext';
import CatalogItemAutocomplete, { type CatalogItemOption } from './CatalogItemAutocomplete';

export type ReplenishmentPolicyRow = {
  id: number;
  minQty: number;
  targetQty: number;
  reorderPoint: number | null;
  safetyStock: number;
  leadTimeDays: number | null;
  receiveLocation?: { id: number; name?: string | null };
  location?: { id: number; name?: string | null };
  inventoryItem?: { id: number; upc: string; name?: string | null };
  preferredVendor?: { id: number; name: string } | null;
};

type Props = {
  locations: Array<{ id: number; name?: string | null }>;
  vendors: Array<{ id: number; name: string }>;
  queryPolicies: (locationId?: number) => Promise<ReplenishmentPolicyRow[]>;
  savePolicy: (payload: Record<string, unknown>) => Promise<void>;
  searchCatalog: (term: string) => Promise<Array<{ id: number; upc: string; name?: string | null }>>;
  locationFieldName?: 'locationId';
};

const ReplenishmentPolicyPanel: React.FC<Props> = ({
  locations,
  vendors,
  queryPolicies,
  savePolicy,
  searchCatalog,
}) => {
  const { showSuccess, showError } = useNotification();
  const [locationFilter, setLocationFilter] = useState<number | ''>('');
  const [rows, setRows] = useState<ReplenishmentPolicyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ReplenishmentPolicyRow | null>(null);
  const [catalogItem, setCatalogItem] = useState<CatalogItemOption | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await queryPolicies(locationFilter === '' ? undefined : locationFilter);
      setRows(data);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to load policies');
    } finally {
      setLoading(false);
    }
  }, [queryPolicies, locationFilter, showError]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const columns = useMemo<MRT_ColumnDef<ReplenishmentPolicyRow>[]>(
    () => [
      {
        header: 'Location',
        Cell: ({ row }) => row.original.receiveLocation?.name ?? row.original.location?.name ?? '—',
      },
      {
        header: 'SKU',
        Cell: ({ row }) => {
          const item = row.original.inventoryItem;
          return item ? `${item.upc}${item.name ? ` — ${item.name}` : ''}` : '—';
        },
      },
      { accessorKey: 'minQty', header: 'Min' },
      { accessorKey: 'reorderPoint', header: 'Reorder' },
      { accessorKey: 'targetQty', header: 'Target' },
      { accessorKey: 'safetyStock', header: 'Safety' },
      { accessorKey: 'leadTimeDays', header: 'Lead (days)' },
      {
        header: 'Vendor',
        Cell: ({ row }) => row.original.preferredVendor?.name ?? '—',
      },
      {
        id: 'actions',
        header: '',
        Cell: ({ row }) => (
          <GenericActionPicker
            actions={[
              {
                label: 'Edit',
                icon: GENERIC_ICON_NAME.EDIT,
                onClick: () => {
                  setEditing(row.original);
                  setCatalogItem(
                    row.original.inventoryItem
                      ? {
                          id: row.original.inventoryItem.id,
                          upc: row.original.inventoryItem.upc,
                          name: row.original.inventoryItem.name ?? null,
                        }
                      : null,
                  );
                  setModalOpen(true);
                },
              },
            ]}
          />
        ),
      },
    ],
    [],
  );

  const fieldGroups = useMemo(
    () => [
      {
        groupName: 'Policy',
        fields: [
          ...(!editing
            ? [
                {
                  label: 'Catalog SKU',
                  name: '_catalog',
                  type: GENERIC_FORM_FIELD_TYPE.REACT_ELEMENT,
                  columns: 12,
                  component: (
                    <CatalogItemAutocomplete
                      value={catalogItem}
                      onChange={setCatalogItem}
                      searchCatalog={searchCatalog}
                    />
                  ),
                },
              ]
            : []),
          {
            label: 'Location',
            name: 'locationId',
            type: GENERIC_FORM_FIELD_TYPE.SINGLE_SELECT,
            required: true,
            columns: 12,
            options: locations.map((l) => ({ label: l.name ?? `Location ${l.id}`, value: l.id })),
          },
          {
            label: 'Min qty',
            name: 'minQty',
            type: GENERIC_FORM_FIELD_TYPE.NUMBER,
            required: true,
            columns: 6,
          },
          {
            label: 'Reorder point',
            name: 'reorderPoint',
            type: GENERIC_FORM_FIELD_TYPE.NUMBER,
            columns: 6,
          },
          {
            label: 'Target qty',
            name: 'targetQty',
            type: GENERIC_FORM_FIELD_TYPE.NUMBER,
            required: true,
            columns: 6,
          },
          {
            label: 'Safety stock',
            name: 'safetyStock',
            type: GENERIC_FORM_FIELD_TYPE.NUMBER,
            columns: 6,
          },
          {
            label: 'Lead time (days)',
            name: 'leadTimeDays',
            type: GENERIC_FORM_FIELD_TYPE.NUMBER,
            columns: 6,
          },
          {
            label: 'Preferred vendor',
            name: 'preferredVendorId',
            type: GENERIC_FORM_FIELD_TYPE.SINGLE_SELECT,
            columns: 6,
            options: [{ label: '(none)', value: '' }, ...vendors.map((v) => ({ label: v.name, value: v.id }))],
          },
        ],
      },
    ],
    [locations, vendors, editing, catalogItem, searchCatalog],
  );

  const initialValues = editing
    ? {
        locationId: editing.receiveLocation?.id ?? editing.location?.id,
        minQty: editing.minQty,
        reorderPoint: editing.reorderPoint ?? editing.minQty,
        targetQty: editing.targetQty,
        safetyStock: editing.safetyStock,
        leadTimeDays: editing.leadTimeDays ?? '',
        preferredVendorId: editing.preferredVendor?.id ?? '',
      }
    : { minQty: 0, reorderPoint: 0, targetQty: 0, safetyStock: 0, leadTimeDays: 7 };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Replenishment policy
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          select
          size="small"
          label="Filter location"
          value={locationFilter}
          onChange={(e) => setLocationFilter(e.target.value === '' ? '' : Number(e.target.value))}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All</MenuItem>
          {locations.map((l) => (
            <MenuItem key={l.id} value={l.id}>
              {l.name ?? `Location ${l.id}`}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="contained"
          onClick={() => {
            setEditing(null);
            setCatalogItem(null);
            setModalOpen(true);
          }}
        >
          Add policy
        </Button>
      </Box>
      <MaterialReactTable columns={columns} data={rows} state={{ isLoading: loading }} enablePagination />
      <GenericFormModal
        visible={modalOpen}
        onDismiss={() => setModalOpen(false)}
        title={editing ? 'Edit policy' : 'Add policy'}
        fieldGroups={fieldGroups}
        initialValues={initialValues}
        onSubmit={async (values) => {
          const itemId = editing?.inventoryItem?.id ?? catalogItem?.id;
          if (!itemId) {
            showError('Select a catalog SKU');
            return;
          }
          try {
            await savePolicy({
              id: editing?.id,
              locationId: Number(values.locationId),
              inventoryItemId: itemId,
              minQty: Number(values.minQty),
              reorderPoint: values.reorderPoint === '' ? null : Number(values.reorderPoint),
              targetQty: Number(values.targetQty),
              safetyStock: Number(values.safetyStock ?? 0),
              leadTimeDays: values.leadTimeDays === '' ? null : Number(values.leadTimeDays),
              preferredVendorId: values.preferredVendorId === '' ? null : Number(values.preferredVendorId),
            });
            showSuccess('Policy saved');
            setModalOpen(false);
            void load();
          } catch (e: unknown) {
            showError(e instanceof Error ? e.message : 'Save failed');
          }
        }}
      />
    </Paper>
  );
};

export default ReplenishmentPolicyPanel;
